import {
  breakpointSizes,
  ButtonDesigns,
  ButtonTypes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardIdFromRoute,
  selectCreditCardUser,
} from 'src/app/Budget/redux/budget.selectors'
import BudgetHistory from 'src/app/BudgetHistory/BudgetHistory'
import BudgetOverview from 'src/app/BudgetOverview/BudgetOverview'
import BudgetTransactions from 'src/app/BudgetTransactions/BudgetTransactions'
import BudgetVirtualCards from 'src/app/BudgetVirtualCards/BudgetVirtualCards'
import FamilyLayout, {
  FamilyLayoutTab,
} from 'src/component-layout/FamilyLayout/FamilyLayout'
import AccountHeader from 'src/components/AccountHeader/AccountHeader'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import Box from 'src/components/Box/Box'
import Button from 'src/components/Button/Button'
import { CreateNewDropdownOption } from 'src/components/CreateNewDropdown/CreateNewDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import SvgStatusSummaryActionRequiredRed from 'src/components/Svgs/SvgStatusSummaryActionRequiredRed'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TeamAvatarsIcon from 'src/components/TeamAvatarsIcon/TeamAvatarsIcon'
import Topnav from 'src/components/Topnav/Topnav'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useIsUserOrglessRCNUser } from 'src/hooks/useOrglessUserStatus'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  OrganizationInviteModel,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import { budgetsRouteTree } from 'src/navigation/budgetsRouteTree'
import { Rdrct } from 'src/navigation/Lnk'
import {
  budgetHistoryUrl,
  budgetOverviewUrl,
  budgetTransactionsUrl,
  budgetUrl,
  budgetVirtualCardsUrl,
  deactivateBudgetUrl,
  manageOrgInviteUrl,
  updateBudgetUrl,
} from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectOrganizationInvitesModelsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadingFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectBudgetHistoryMatch,
  selectBudgetOverviewMatch,
  selectBudgetTransactionsMatch,
  selectBudgetVirtualCardsMatch,
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

// Stable test ids per entity sub-tab (preserve legacy selectors).
const ENTITY_TAB_TEST_IDS: Record<string, string> = {
  overview: 'overviewTab',
  virtualcards: 'virtualCardsTab',
  transactions: 'transactionsTab',
  history: 'historyTab',
}

/**
 * BudgetEntityPage — route-level page for a single budget's sub-tabs (EX-39748).
 *
 * Owns entity routing + data: resolves the budget id from the route, loads the
 * credit card + permissions, resolves the active sub-tab, and renders the
 * persistent FamilyLayout tab bar with a nested <Switch> beneath it. Tab content
 * components (BudgetOverview/VirtualCards/Transactions/History) stay routing-free
 * and resolve their own data from the unchanged route via existing selectors.
 *
 * Gate: registered once for the whole `/budgets/:id` family (see
 * flatArchitectureRoutes.tsx), so this page + its FamilyLayout Subnav stay
 * mounted across sub-tabs and the sliding active bar animates.
 */
const BudgetEntityPage: React.FC = () => {
  const translate = useTranslate()
  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const { data: creditCard, isLoading: isLoadingCreditCard } =
    useQueryCreditCard({
      creditCardId: creditCardId,
    })
  const {
    data: creditCardPermissions,
    isLoading: isLoadingCreditCardPermissions,
  } = useQueryCreditCardPermissions({
    creditCardId: creditCardId,
    retry: false,
  })

  const creditCardUser = useSelector(selectCreditCardUser)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const budgetOverviewMatch = useSelector(selectBudgetOverviewMatch)
  const budgetVirtualCardsMatch = useSelector(selectBudgetVirtualCardsMatch)
  const budgetTransactionsMatch = useSelector(selectBudgetTransactionsMatch)
  const budgetHistoryMatch = useSelector(selectBudgetHistoryMatch)
  const isUserOrglessRCNUser = useIsUserOrglessRCNUser()
  const currentLocation = useSelector(selectLocationFromState)
  const loadingRemoteConfig = useSelector(selectLoadingFromRemoteConfigState)
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)

  let budgetAndInviteOrg: OrganizationsListResponse | undefined
  let correspondingInviteId
  if (creditCard !== undefined) {
    Object.values(orgInvites).forEach((invite: OrganizationInviteModel) => {
      if (invite.organization.id === creditCard.organizationId) {
        budgetAndInviteOrg = invite.organization
        correspondingInviteId = invite.id
      }
    })
  }

  const { analytics } = useAnalyticsContext()

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard
  const canIssueVirtualCards = creditCardPermissions?.canIssueVirtualCards
  const canViewBudget = creditCardPermissions?.canRead

  const dispatch = useBackgroundDispatch()

  const tabs: FamilyLayoutTab[] = React.useMemo(() => {
    if (!creditCard) return []

    return budgetsRouteTree.entity.tabs.map((tab) => ({
      key: tab.key,
      label: translate(tab.labelSource),
      to: tab.to(creditCard.id),
      ariaLabelSource: tab.labelSource,
      testId: ENTITY_TAB_TEST_IDS[tab.key],
    }))
  }, [creditCard?.id])

  const activeTabKey = budgetOverviewMatch
    ? 'overview'
    : budgetVirtualCardsMatch
      ? 'virtualcards'
      : budgetTransactionsMatch
        ? 'transactions'
        : budgetHistoryMatch
          ? 'history'
          : undefined

  const updateBudgetTopnavOption: CreateNewDropdownOption[] | undefined =
    creditCard !== undefined && canUpdateCreditCard
      ? isUserOrglessRCNUser
        ? [
            {
              label: translate('Update Budget'),
              to: currentLocation.pathname,
              onClick: (): void => {
                dispatch(
                  createSetIsSetupOrganizationPopupOpen(
                    true,
                    updateBudgetUrl(creditCard.id),
                  ),
                )
              },
              ariaLabelSource: 'Update Budget',
              image: <SvgUpdate />,
            },
          ]
        : [
            {
              label: translate('Update Budget'),
              to: updateBudgetUrl(creditCard.id),
              onClick: (): void => {
                analytics?.track(
                  'budget detail header - open update budget form - button press',
                  {
                    intent: 'open update budget form',
                    interaction: 'button press',
                    screen: budgetOverviewUrl(':id'),
                  },
                )
              },
              ariaLabelSource: 'Update Budget',
              image: <SvgUpdate />,
            },
          ]
      : undefined
  const positiveActionOptions = [...(updateBudgetTopnavOption || [])]

  const negativeActionOptions =
    creditCard !== undefined && canUpdateCreditCard && creditCard?.isActive
      ? [
          {
            label: translate('Deactivate Budget'),
            to: deactivateBudgetUrl(creditCard.id),
            onClick: (): void => {
              analytics?.track('deactivate budget', {
                budget_id: creditCard.id,
                issuer: creditCard.issuer,
              })
            },
            ariaLabelSource: translateSingleString(locale, 'Deactivate Budget'),
            image: <SvgTrash />,
          },
        ]
      : []

  const isLoading = (): boolean | undefined =>
    isLoadingCreditCard || isLoadingCreditCardPermissions

  return isLoading() || loadingRemoteConfig ? (
    <LoadingBackgroundPage />
  ) : creditCard !== undefined ? (
    <BackgroundPage>
      <Topnav
        titleElements={{
          title:
            (creditCard.budgetUsers?.length ?? 0) > 1 ? (
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  gap: 8px;
                `}
              >
                <TeamAvatarsIcon users={creditCard.budgetUsers} />
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.black,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {translate('Team Budget')}
                </span>
              </div>
            ) : !canIssueVirtualCards && creditCardUser !== undefined ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <UserAvatar
                  size={AvatarSizes.Small}
                  user={creditCardUser}
                  css={css`
                    margin-right: 10px;
                  `}
                />
                {translate("${firstName}'s Budget", {
                  firstName: creditCardUser.firstNameOrEmail,
                })}
              </div>
            ) : (
              translate('Your Budget')
            ),
          scrollContent: (
            <span
              css={css`
                font-size: 14px;
                line-height: 18px;
                ${!canIssueVirtualCards &&
                css`
                  margin-left: 32px;
                `}
              `}
            >
              {creditCard.truncatedDisplayName}
            </span>
          ),
        }}
        alternativeActionOptions={[
          {
            title: translate('Budget Options'),
            positiveActionOptions: positiveActionOptions,
            negativeActionOptions: negativeActionOptions,
          },
        ]}
      />
      <AccountHeader creditCard={creditCard} />
      <FamilyLayout
        tabs={canViewBudget ? tabs : []}
        activeTabKey={activeTabKey}
        alignWithHeader
      >
        {creditCardPermissions !== undefined && !canViewBudget ? (
          <Box
            css={css`
              margin-top: 40px;
              max-width: 500px;
              align-self: center;
              padding: 40px;
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <SvgStatusSummaryActionRequiredRed
              css={css`
                margin-bottom: 32px;
              `}
            />
            <div
              css={css`
                ${getFontCss(
                  20,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
                position: relative;
                margin-bottom: 20px;
                display: flex;
                flex-direction: column;
                flex-shrink: 0;

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  padding: 20px 30px;
                `)}
              `}
            >
              {translate('Action Required')}
            </div>
            <div
              css={css`
                overflow-wrap: break-word;
                text-align: center;
              `}
            >
              {translate(
                'As a budget user, you can create and update virtual cards on this budget. To access these features, ${cta}.',
                {
                  cta:
                    budgetAndInviteOrg !== undefined
                      ? translate('accept your invite to join ${orgName}', {
                          orgName: budgetAndInviteOrg.name,
                        })
                      : translate(
                          'please reach out to the Owner or Admin to invite you to the appropriate org',
                        ),
                },
              )}
            </div>
            {correspondingInviteId !== undefined && (
              <Button
                design={ButtonDesigns.designCta}
                type={ButtonTypes.button}
                text={translate('Review the Invite')}
                linkProps={{
                  to: manageOrgInviteUrl(correspondingInviteId),
                  ariaLabelSource: 'Review the Invite',
                }}
                big={true}
                css={css`
                  width: 315px;
                  margin-top: 40px;
                `}
              />
            )}
          </Box>
        ) : (
          <Switch location={calculatedBackgroundLocation}>
            <Route exact path={budgetOverviewUrl(':id')}>
              <BudgetOverview />
            </Route>
            <Route exact path={budgetVirtualCardsUrl(':id')}>
              <BudgetVirtualCards />
            </Route>
            <Route exact path={budgetTransactionsUrl(':id')}>
              <BudgetTransactions />
            </Route>
            <Route exact path={budgetHistoryUrl(':id')}>
              <BudgetHistory />
            </Route>
            <Route exact path={budgetUrl(':id')}>
              <Rdrct to={budgetsRouteTree.entity.redirectTo(creditCard.id)} />
            </Route>
            <Route path={'/'}>
              <NotFoundBackgroundPageContent />
            </Route>
          </Switch>
        )}
      </FamilyLayout>
    </BackgroundPage>
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default BudgetEntityPage
